import { PrivacyBlur } from "#components/privacy-blur"
import { useTheme } from "#components/theme-provider"
import { Card, CardContent, CardTitle } from "#components/ui/card"
import { Body, MetricValue, StatLabel, StatValue } from "#components/ui/typography"
import { useCurrencyFormatters } from "#hooks/use-currency-formatters"
import { useI18n } from "#hooks/use-i18n"
import { useThemeColors } from "#hooks/use-theme-colors"
import { cardEntrance, getCardHoverEffect, statValue } from "#lib/animations"
import type { DashboardAnalyticsResponse } from "@home-finance-manager/shared/types/dashboard"
import { motion } from "framer-motion"
import React from "react"
import { Area, AreaChart, ResponsiveContainer, Tooltip } from "recharts"

interface MonthlyBalanceCardProps {
  analytics: DashboardAnalyticsResponse | undefined
}

export const MonthlyBalanceCard = React.memo<MonthlyBalanceCardProps>(({ analytics }) => {
  const { t } = useI18n()
  const { formatCurrency } = useCurrencyFormatters()
  const themeColors = useThemeColors()
  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"
  const monthlyBalances = analytics?.monthlyBalances || []

  // Get the most recent month's data (last in the array, as they're sorted chronologically)
  const selectedMonthIndex = React.useMemo(() => {
    if (monthlyBalances.length === 0) return -1
    return monthlyBalances.length - 1
  }, [monthlyBalances.length])

  const currentBalance =
    selectedMonthIndex >= 0 && monthlyBalances[selectedMonthIndex]
      ? monthlyBalances[selectedMonthIndex].balance
      : monthlyBalances.length > 0
        ? monthlyBalances[monthlyBalances.length - 1].balance
        : 0

  // Use prepared data directly (already sorted chronologically, for months in date range)
  const chartData = monthlyBalances.map((b) => ({
    month: b.monthLabel.split(" ")[0],
    monthLabel: b.monthLabel,
    balance: b.balance,
  }))

  const chartColor = themeColors.primary

  const isPositive = currentBalance >= 0
  const balanceColor = isPositive ? "text-green-600" : "text-destructive"

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={cardEntrance}
      whileHover={getCardHoverEffect(isDark)}
      className="h-full"
    >
      <Card className="flex flex-col overflow-hidden h-full max-h-full border-border/50 bg-card/50 backdrop-blur-sm shadow-sm dark:shadow-none dark:border-border/50 transition-shadow hover:shadow-md dark:hover:shadow-none">
        {/* Accent border */}
        <div
          className={`h-1 absolute top-0 left-0 right-0 ${
            isPositive
              ? isDark
                ? "bg-gradient-to-r from-green-600/60 via-green-600/40 to-transparent"
                : "bg-gradient-to-r from-green-600/80 via-green-600/60 to-transparent"
              : isDark
                ? "bg-gradient-to-r from-destructive/60 via-destructive/40 to-transparent"
                : "bg-gradient-to-r from-destructive/80 via-destructive/60 to-transparent"
          }`}
        />
        <CardContent className="p-6 flex-1 flex flex-col min-h-0">
          <div className="flex items-start justify-between mb-4 flex-shrink-0">
            <div className="flex-1">
              <CardTitle className="text-base font-medium mb-3 text-foreground">
                {t("components.dashboard.monthlyBalance.title")}
              </CardTitle>
              <motion.div variants={statValue}>
                <StatValue className={`text-3xl sm:text-4xl mb-2 ${balanceColor}`}>
                  {formatCurrency(currentBalance)}
                </StatValue>
              </motion.div>
              <StatLabel>{t("components.dashboard.monthlyBalance.currentMonthBalance")}</StatLabel>
            </div>
          </div>

          {/* Premium Area Chart */}
          {chartData.length > 0 ? (
            <div className="h-32 mb-4 -mx-2 relative flex-shrink-0">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 5 }}>
                  <defs>
                    <linearGradient id="balanceGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={chartColor} stopOpacity={0.4} />
                      <stop offset="50%" stopColor={chartColor} stopOpacity={0.15} />
                      <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Tooltip
                    formatter={(value: number) => (
                      <PrivacyBlur>{formatCurrency(value)}</PrivacyBlur>
                    )}
                    labelFormatter={(label, payload) => {
                      const data = payload?.[0]?.payload
                      return data?.monthLabel || label
                    }}
                    contentStyle={{
                      backgroundColor: themeColors.card,
                      border: `1px solid ${themeColors.border}`,
                      borderRadius: "6px",
                      padding: "8px 12px",
                      color: themeColors.cardForeground,
                      boxShadow:
                        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                    }}
                    wrapperStyle={{
                      outline: "none",
                    }}
                    itemStyle={{
                      color: themeColors.cardForeground,
                    }}
                    cursor={{
                      stroke: chartColor,
                      strokeWidth: 1,
                      strokeDasharray: "3 3",
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="balance"
                    stroke={chartColor}
                    strokeWidth={2.5}
                    fill="url(#balanceGradient)"
                    strokeLinecap="round"
                  />
                </AreaChart>
              </ResponsiveContainer>
              {/* Glow effect */}
              <div
                className="absolute inset-0 pointer-events-none opacity-20"
                style={{
                  background: `radial-gradient(ellipse at center, ${chartColor} 0%, transparent 70%)`,
                }}
              />
            </div>
          ) : (
            <div className="h-32 bg-muted/10 rounded-md flex items-center justify-center mb-4 border border-border/50">
              <Body className="text-muted-foreground text-sm">No data</Body>
            </div>
          )}

          {/* Monthly Breakdown */}
          <div className="flex-1 min-h-0 flex flex-col pt-4 border-t border-border/50">
            <div className="flex-1 overflow-y-auto min-h-0 space-y-2 pr-1">
              {monthlyBalances.length > 0 ? (
                monthlyBalances
                  .slice()
                  .reverse()
                  .map((balance) => (
                    <div
                      key={balance.month}
                      className="flex items-center justify-between text-sm group hover:bg-muted/30 rounded-md px-2 py-1.5 transition-colors flex-shrink-0"
                    >
                      <span className="text-muted-foreground font-medium">
                        {balance.monthLabel.split(" ")[0]}
                      </span>
                      <MetricValue
                        className={balance.balance >= 0 ? "text-green-600" : "text-destructive"}
                      >
                        {balance.balance >= 0 ? "+" : ""}
                        {formatCurrency(balance.balance)}
                      </MetricValue>
                    </div>
                  ))
              ) : (
                <div className="text-sm text-muted-foreground">
                  {t("components.dashboard.monthlyBalance.noData")}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
})

MonthlyBalanceCard.displayName = "MonthlyBalanceCard"
